/**
 * Adaptive spaced-repetition engine for the GMAT flashcards feature.
 *
 * A lightweight SM-2 / Leitner hybrid:
 *  - New cards must be answered correctly twice before graduating out of
 *    "learning" into "review" (short-term reinforcement).
 *  - Correct review answers grow the interval by the ease factor; enough
 *    correct reviews in a row and the card is "mastered".
 *  - A wrong answer drops the card back into "learning" and resurfaces it
 *    soon — the "try again after a while" behavior — while nudging the
 *    ease factor down so it's reviewed more often going forward.
 *  - Picking the next card is weighted-random, not a fixed queue: due /
 *    overdue / previously-wrong cards are more likely to be drawn, but the
 *    exact order is randomized each time, and a card is never repeated
 *    back-to-back.
 */
import type { CardProgress, Flashcard, ProgressMap } from './types';

const MINUTE = 60 * 1000;
const DAY = 24 * 60 * 60 * 1000;

const DEFAULT_EASE = 2.5;
const MIN_EASE = 1.3;
const MAX_EASE = 3.0;
const MASTER_INTERVAL_DAYS = 21;
const LEARNING_REQUEUE_MINUTES = 10;
const LAPSE_REQUEUE_MINUTES = 6;
const MIN_GAP_BEFORE_REPEAT = 3; // cards seen before a just-answered card can reappear

export function createProgress(cardId: string, now = Date.now()): CardProgress {
  return {
    cardId,
    state: 'new',
    easeFactor: DEFAULT_EASE,
    intervalDays: 0,
    dueAt: now,
    reps: 0,
    lapses: 0,
    streak: 0,
  };
}

/** Returns the next progress record after grading a card correct or wrong. */
export function gradeCard(progress: CardProgress, correct: boolean, now = Date.now()): CardProgress {
  const reps = progress.reps + 1;

  if (!correct) {
    return {
      ...progress,
      state: 'learning',
      reps,
      lapses: progress.lapses + 1,
      streak: 0,
      easeFactor: Math.max(MIN_EASE, progress.easeFactor - 0.2),
      intervalDays: 0,
      dueAt: now + LAPSE_REQUEUE_MINUTES * MINUTE,
      lastResult: 'wrong',
      lastReviewedAt: now,
    };
  }

  const streak = progress.streak + 1;

  // Learning phase: needs two correct answers in a row to graduate to "review".
  if (progress.state === 'new' || progress.state === 'learning') {
    if (streak >= 2) {
      return {
        ...progress,
        state: 'review',
        reps,
        streak,
        intervalDays: 1,
        dueAt: now + 1 * DAY,
        lastResult: 'correct',
        lastReviewedAt: now,
      };
    }
    return {
      ...progress,
      state: 'learning',
      reps,
      streak,
      dueAt: now + LEARNING_REQUEUE_MINUTES * MINUTE,
      lastResult: 'correct',
      lastReviewedAt: now,
    };
  }

  // Review / mastered phase: grow the interval using the ease factor.
  const easeFactor = Math.min(MAX_EASE, progress.easeFactor + 0.1);
  const intervalDays = Math.max(1, Math.round(progress.intervalDays * easeFactor));
  const state = intervalDays >= MASTER_INTERVAL_DAYS ? 'mastered' : 'review';

  return {
    ...progress,
    state,
    reps,
    streak,
    easeFactor,
    intervalDays,
    dueAt: now + intervalDays * DAY,
    lastResult: 'correct',
    lastReviewedAt: now,
  };
}

interface PickOptions {
  cards: Flashcard[];
  progressMap: ProgressMap;
  recentCardIds: string[]; // most-recent-first, used to avoid back-to-back repeats
  now?: number;
}

/**
 * Weighted-random selection of the next card to show. Due/overdue and
 * previously-wrong cards are weighted heavier, new cards get a modest
 * constant weight so discovery keeps pace with review, and anything seen
 * within the last MIN_GAP_BEFORE_REPEAT cards is excluded when possible.
 */
export function pickNextCard({ cards, progressMap, recentCardIds, now = Date.now() }: PickOptions): Flashcard | null {
  if (cards.length === 0) return null;

  const cooldown = new Set(recentCardIds.slice(0, MIN_GAP_BEFORE_REPEAT));
  const candidates = cards.filter(c => !cooldown.has(c.id));
  const pool = candidates.length > 0 ? candidates : cards;

  const weighted = pool.map(card => {
    const progress = progressMap[card.id];
    let weight = 1;

    if (!progress || progress.state === 'new') {
      weight = 3;
    } else {
      const overdueMs = now - progress.dueAt;
      if (overdueMs >= 0) {
        // Overdue cards get progressively heavier, extra weight for past lapses.
        weight = 6 + Math.min(10, overdueMs / DAY) + progress.lapses * 2;
      } else {
        // Not due yet — still eligible (keeps sessions feeling random/full),
        // but heavily deprioritized versus anything due.
        weight = 0.15;
      }
      if (progress.state === 'mastered') weight *= 0.3;
    }

    return { card, weight };
  });

  const totalWeight = weighted.reduce((sum, w) => sum + w.weight, 0);
  let roll = Math.random() * totalWeight;

  for (const { card, weight } of weighted) {
    roll -= weight;
    if (roll <= 0) return card;
  }

  return weighted[weighted.length - 1].card;
}

export function isDue(progress: CardProgress | undefined, now = Date.now()): boolean {
  if (!progress) return true;
  return progress.dueAt <= now;
}

export const SRS_TUNING = {
  MASTER_INTERVAL_DAYS,
  MIN_GAP_BEFORE_REPEAT,
};
